import { UFS, validarHaras } from '@equiview/domain';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { SemCadastro } from '../components/SemCadastro.js';
import {
  BarraAcoes,
  Botao,
  BotaoLink,
  CabecalhoPagina,
  Campo,
  CampoTexto,
  classeCampo,
  OpcoesRadio,
  Painel,
  ResumoErros,
  Selecao,
  useAvisoSaida,
} from '../components/ui/index.js';
import { useFonte, usePodeCadastrar } from '../dados/contexto.js';
import type { Haras, HarasEntrada } from '../dados/fonte.js';

const VAZIO: HarasEntrada = {
  name: '',
  city: null,
  uf: null,
  abccmm_code: null,
  owner_contact: null,
  data_sharing_level: 'privado',
  notes: null,
};

const EXPLICACAO = {
  privado: 'Os dados do haras só entram em relatórios do próprio haras.',
  agregado: 'Entram em estatísticas gerais da raça, sem identificar o haras nem os animais.',
  publico: 'Resultados podem ser publicados com o nome do haras e dos animais (ex.: avaliação de progênie).',
} as const;

/** Cadastro e edicao de haras. */
export function FormHaras() {
  const { id } = useParams();
  const fonte = useFonte();
  const equipe = usePodeCadastrar();
  const navegar = useNavigate();
  const [dados, setDados] = useState<HarasEntrada>(VAZIO);
  const [original, setOriginal] = useState<string>(JSON.stringify(VAZIO));
  const [tentou, setTentou] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [carregado, setCarregado] = useState(!id);
  const nome = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!id) return;
    void fonte.listarHaras().then((lista) => {
      const h = lista.find((x: Haras) => x.id === id);
      if (h) {
        setDados(h);
        setOriginal(JSON.stringify(h));
      }
      setCarregado(true);
    });
  }, [fonte, id]);

  const sujo = JSON.stringify(dados) !== original && !salvando;
  useAvisoSaida(sujo);

  if (!fonte.cadastro || !equipe) return <SemCadastro demo={fonte.ficticia} />;
  if (!carregado) return null;

  const { erros } = validarHaras(dados);
  const erro = (campo: string) => (tentou ? erros.find((e) => e.campo === campo)?.mensagem : undefined);
  const mudar = <K extends keyof HarasEntrada>(k: K, v: HarasEntrada[K]) => setDados((d) => ({ ...d, [k]: v }));

  const salvar = async () => {
    setTentou(true);
    if (erros.length) {
      nome.current?.focus();
      return;
    }
    setSalvando(true);
    await fonte.cadastro!.salvarHaras(dados);
    setOriginal(JSON.stringify(dados));
    navegar(id ? '/haras' : '/animais');
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void salvar();
      }}
      className="space-y-6"
      noValidate
    >
      <CabecalhoPagina
        trilha={[{ rotulo: 'Haras', para: '/haras' }, { rotulo: id ? 'Editar' : 'Novo haras' }]}
        titulo={id ? dados.name || 'Editar haras' : 'Novo haras'}
        meta={<span>Campos com * são obrigatórios.</span>}
      />

      {tentou && <ResumoErros erros={erros} />}

      <div className="grid gap-6 lg:grid-cols-2">
        <Painel titulo="Identificação">
          <div className="space-y-4">
            <CampoTexto
              ref={nome}
              rotulo="Nome do haras"
              obrigatorio
              autoFocus={!id}
              value={dados.name}
              onChange={(e) => mudar('name', e.target.value)}
              erro={erro('name')}
            />
            <CampoTexto
              rotulo="Código ABCCMM"
              ajuda="Código do criador na associação, se houver."
              value={dados.abccmm_code ?? ''}
              onChange={(e) => mudar('abccmm_code', e.target.value)}
            />
          </div>
        </Painel>

        <Painel titulo="Localização e responsável">
          <div className="space-y-4">
            <div className="grid grid-cols-[minmax(0,1fr)_7rem] gap-3">
              <CampoTexto rotulo="Cidade" value={dados.city ?? ''} onChange={(e) => mudar('city', e.target.value)} />
              <Selecao rotulo="UF" value={dados.uf ?? ''} onChange={(e) => mudar('uf', e.target.value || null)} erro={erro('uf')}>
                <option value="">—</option>
                {UFS.map((uf) => (
                  <option key={uf} value={uf}>
                    {uf}
                  </option>
                ))}
              </Selecao>
            </div>
            <CampoTexto
              rotulo="Contato do responsável"
              ajuda="Nome e telefone ou e-mail de quem recebe a equipe."
              value={dados.owner_contact ?? ''}
              onChange={(e) => mudar('owner_contact', e.target.value)}
            />
          </div>
        </Painel>

        <Painel titulo="Compartilhamento dos dados" className="lg:col-span-2">
          <div className="space-y-3">
            <OpcoesRadio
              rotulo="Nível de compartilhamento"
              obrigatorio
              valor={dados.data_sharing_level}
              onMudar={(v) => mudar('data_sharing_level', v)}
              opcoes={[
                { valor: 'privado', rotulo: 'Privado' },
                { valor: 'agregado', rotulo: 'Agregado' },
                { valor: 'publico', rotulo: 'Público' },
              ]}
              ajuda={EXPLICACAO[dados.data_sharing_level]}
            />
            <p className="border-l-2 border-aviso bg-aviso/8 px-3 py-2 text-sm">
              O contrato com o haras precisa dizer o mesmo. Se o haras puder vetar a publicação de um resultado
              desfavorável de progênie, a avaliação de progênie (Fase 3) deixa de existir.
            </p>
          </div>
        </Painel>

        <Painel titulo="Observações" className="lg:col-span-2">
          <Campo rotulo="Observações">
            {(a) => (
              <textarea
                {...a}
                rows={3}
                value={dados.notes ?? ''}
                onChange={(e) => mudar('notes', e.target.value)}
                className={`${classeCampo()} resize-y`}
              />
            )}
          </Campo>
        </Painel>
      </div>

      <BarraAcoes aviso={sujo ? 'Alterações não salvas' : undefined}>
        <BotaoLink to={id ? '/haras' : '/animais'}>Cancelar</BotaoLink>
        <Botao type="submit" variante="principal" carregando={salvando}>
          {id ? 'Salvar alterações' : 'Cadastrar haras'}
        </Botao>
      </BarraAcoes>
    </form>
  );
}
